import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { api } from './client';
import { queryKeys } from './queries';
import type { CreateFromSheetInput } from './types';

/** Connections belong to the signed-in person, not to a workspace. */
export const googleConnectionsKey = ['account', 'google', 'connections'] as const;

export function useGoogleConnections() {
  return useQuery({
    queryKey: googleConnectionsKey,
    queryFn: ({ signal }) => api.listGoogleConnections(signal)
  });
}

export function useConnectGoogle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { returnTo: string }) => api.connectGoogle(v.returnTo),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: googleConnectionsKey });
    }
  });
}

export function useDisconnectGoogle() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => api.disconnectGoogle(),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: googleConnectionsKey });
    }
  });
}

export function usePickerToken() {
  return useMutation({ mutationFn: () => api.googlePickerToken() });
}

export function useDescribeSheet() {
  return useMutation({ mutationFn: (v: { fileId: string }) => api.describeSheet(v.fileId) });
}

export function usePreviewSheet() {
  return useMutation({ mutationFn: (v: { fileId: string; sheetId: number }) => api.previewSheet(v.fileId, v.sheetId) });
}

// A link belongs to a dataset, and the hooks below do not know which report (or workspace) shows it, so a
// link change marks every report detail and every reports list stale. Not awaited, as in report-queries.
function invalidateReports(client: QueryClient): void {
  void client.invalidateQueries({
    predicate: (query) => query.queryKey[0] === 'workspaces' && query.queryKey[2] === 'reports'
  });
}

export function useCreateReportFromSheet(workspaceId: string) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateFromSheetInput) => api.createReportFromSheet(input),
    onSuccess: () => {
      void client.invalidateQueries({ queryKey: queryKeys.reports(workspaceId) });
    }
  });
}

export function useSaveLink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { datasetId: string; ref: { fileId: string; sheetId: number } | null }) =>
      api.saveDatasetLink(v.datasetId, v.ref),
    onSuccess: () => invalidateReports(client)
  });
}

export function useUnlink() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (v: { datasetId: string }) => api.removeDatasetLink(v.datasetId),
    onSuccess: () => invalidateReports(client)
  });
}

export function usePullLink() {
  const client = useQueryClient();
  return useMutation({
    // A fresh id per call: the server dedupes a retried request by it, so a second click must not reuse one.
    mutationFn: (v: { datasetId: string }) => api.pullDatasetLink(v.datasetId, crypto.randomUUID()),
    onSuccess: () => invalidateReports(client)
  });
}
